import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Phone,
  Check,
  ChevronRight,
  Eye,
  EyeOff,
  Loader2,
  Zap,
  Calendar,
  MessageSquare,
  RefreshCw,
  Building2,
  Globe,
  CheckCircle,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("clinic_admin_token")}` };
}

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
];

const TOTAL_STEPS = 5;

/* ── Progress bar ── */
function ProgressBar({ current }) {
  return (
    <div className="flex items-center gap-0 mb-10">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center flex-1 last:flex-none">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 border-2 transition-all duration-300
                ${done ? "bg-gradient-to-br from-cyan-500 to-violet-600 border-transparent text-white" : ""}
                ${active ? "border-cyan-500 text-cyan-400 bg-cyan-500/10" : ""}
                ${!done && !active ? "border-white/10 text-slate-600 bg-white/5" : ""}
              `}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : step}
            </div>
            {step < TOTAL_STEPS && (
              <div className="flex-1 h-0.5 mx-1">
                <div
                  className={`h-full transition-all duration-500 rounded-full ${
                    done ? "bg-gradient-to-r from-cyan-500 to-violet-600" : "bg-white/10"
                  }`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Step card wrapper ── */
function StepCard({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.35 }}
      className="bg-[#161B27] border border-white/10 rounded-xl p-8 shadow-2xl shadow-black/40"
    >
      {children}
    </motion.div>
  );
}

/* ── Shared label ── */
function Label({ children }) {
  return (
    <label className="text-xs font-medium text-slate-400 block mb-1.5 uppercase tracking-wider">
      {children}
    </label>
  );
}

/* ── Shared input class ── */
const inputCls =
  "bg-[#0A0D14] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none rounded-lg px-3 py-2 text-sm w-full transition-all";

/* ── Shared back button ── */
function BackButton({ onBack }) {
  if (!onBack) return null;
  return (
    <button
      type="button"
      onClick={onBack}
      className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-300 transition-colors"
    >
      ← Back
    </button>
  );
}

/* ── Step 1: Clinic Details ── */
function Step1({ data, setData, onContinue, onBack }) {
  const set = (f) => (e) => setData((p) => ({ ...p, [f]: e.target.value }));
  return (
    <StepCard>
      <h2 className="text-xl font-bold text-white font-outfit mb-1">
        Tell us about your clinic
      </h2>
      <p className="text-slate-400 text-sm mb-6">
        This info will be used to personalise your AI assistant.
      </p>
      <div className="space-y-4">
        <div>
          <Label>Clinic Name</Label>
          <input
            type="text"
            value={data.clinic_name}
            onChange={set("clinic_name")}
            placeholder="Sunrise Medical Center"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Phone Number</Label>
          <input
            type="tel"
            value={data.phone}
            onChange={set("phone")}
            placeholder="+1 (415) 555-0100"
            className={inputCls}
          />
        </div>
        <div>
          <Label>Address</Label>
          <textarea
            rows={2}
            value={data.address}
            onChange={set("address")}
            placeholder="123 Main St, San Francisco, CA 94105"
            className={`${inputCls} resize-none`}
          />
        </div>
        <div>
          <Label>Timezone</Label>
          <select
            value={data.timezone}
            onChange={set("timezone")}
            className={`${inputCls} appearance-none cursor-pointer`}
          >
            <option value="" disabled className="text-slate-600 bg-[#0A0D14]">
              Select timezone
            </option>
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value} className="bg-[#0A0D14] text-white">
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={onContinue}
          className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </button>
        <BackButton onBack={onBack} />
      </div>
    </StepCard>
  );
}

/* ── Step 2: Connect Vapi ── */
function Step2({ data, setData, onContinue, onSkip, onBack }) {
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // null | "ok" | "error"
  const [testMsg, setTestMsg] = useState("");
  const [creating, setCreating] = useState(false);

  const handleTest = async () => {
    if (!data.vapi_key) return toast.error("Enter your Vapi API key first.");
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(
        `${API}/vapi/test-connection`,
        { api_key: data.vapi_key },
        { headers: authHeader() }
      );
      setTestResult("ok");
      setTestMsg(`Connected! Found ${res.data?.assistant_count ?? 0} assistants.`);
      setData((p) => ({ ...p, vapi_tested: true }));

      // Save key to settings so step 3 can use it via the saved key path too
      try {
        await axios.put(
          `${API}/settings`,
          { vapi_private_api_key: data.vapi_key },
          { headers: authHeader() }
        );
      } catch (_) {
        // non-fatal — step 3 will pass key directly anyway
      }

      // Auto-create assistant
      setCreating(true);
      try {
        await axios.post(
          `${API}/vapi/create-assistant`,
          { api_key: data.vapi_key },
          { headers: authHeader() }
        );
      } catch (_) {
        // non-fatal
      } finally {
        setCreating(false);
      }
    } catch (err) {
      setTestResult("error");
      setTestMsg(err.response?.data?.detail || "Could not connect. Check your key.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <StepCard>
      <h2 className="text-xl font-bold text-white font-outfit mb-1">
        Connect your Vapi account
      </h2>
      <p className="text-slate-400 text-sm mb-1">
        Get your Private API Key from{" "}
        <a
          href="https://dashboard.vapi.ai"
          target="_blank"
          rel="noreferrer"
          className="text-cyan-400 hover:underline"
        >
          dashboard.vapi.ai
        </a>{" "}
        → Account → API Keys
      </p>
      <div className="mt-6 space-y-4">
        <div>
          <Label>Vapi Private API Key</Label>
          <div className="relative">
            <input
              type={showKey ? "text" : "password"}
              value={data.vapi_key}
              onChange={(e) => setData((p) => ({ ...p, vapi_key: e.target.value }))}
              placeholder="vapi_••••••••••••••••••••"
              className={`${inputCls} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowKey((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <button
          onClick={handleTest}
          disabled={testing || !data.vapi_key}
          className="flex items-center gap-2 bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 px-4 py-2 rounded-lg text-sm disabled:opacity-50 transition-all"
        >
          {testing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Test Connection
        </button>

        {testResult === "ok" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2"
          >
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {testMsg}
            {creating && <Loader2 className="w-3.5 h-3.5 animate-spin ml-1 text-slate-400" />}
          </motion.div>
        )}
        {testResult === "error" && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2"
          >
            <X className="w-4 h-4 flex-shrink-0" />
            {testMsg}
          </motion.div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={onContinue}
          disabled={!data.vapi_tested}
          className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold px-6 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </button>
        <BackButton onBack={onBack} />
        <button
          onClick={onSkip}
          className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
        >
          Skip for now
        </button>
      </div>
    </StepCard>
  );
}

/* ── Step 3: Phone Number ── */
function Step3({ data, setData, vapiKey, onContinue, onSkip, onBack }) {
  const [mode, setMode] = useState("provision"); // "provision" | "existing" | "manual"
  const [areaCode, setAreaCode] = useState("415");
  const [provisioning, setProvisioning] = useState(false);
  const [provisionedNumber, setProvisionedNumber] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [manualNumber, setManualNumber] = useState("");

  const fetchNumbers = async () => {
    setLoadingNumbers(true);
    try {
      const params = vapiKey ? { api_key: vapiKey } : {};
      const { data: res } = await axios.get(`${API}/vapi/phone-numbers`, {
        headers: authHeader(),
        params,
      });
      const list = Array.isArray(res) ? res : (res?.data ?? res?.numbers ?? res?.results ?? []);
      setPhoneNumbers(list);
    } catch (_) {
      toast.error("Could not load phone numbers.");
    } finally {
      setLoadingNumbers(false);
    }
  };

  useEffect(() => {
    if (mode === "existing") fetchNumbers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, vapiKey]);

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const body = { area_code: areaCode };
      if (vapiKey) body.api_key = vapiKey;
      const res = await axios.post(
        `${API}/vapi/provision-phone`,
        body,
        { headers: authHeader() }
      );
      const num = res.data?.phone_number ?? res.data?.number ?? "";
      setProvisionedNumber(num);
      setData((p) => ({ ...p, phone_number: num, phone_configured: true }));
      toast.success("Number provisioned!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to provision number.");
    } finally {
      setProvisioning(false);
    }
  };

  const RadioCard = ({ value, icon: Icon, title, subtitle }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={`w-full text-left flex items-start gap-3 p-4 rounded-xl border transition-all ${
        mode === value
          ? "border-cyan-500/50 bg-cyan-500/5"
          : "border-white/10 bg-white/3 hover:border-white/20"
      }`}
    >
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
          mode === value ? "border-cyan-400" : "border-slate-600"
        }`}
      >
        {mode === value && (
          <div className="w-2.5 h-2.5 rounded-full bg-cyan-400" />
        )}
      </div>
      <div>
        <p className="text-white text-sm font-semibold">{title}</p>
        <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>
      </div>
    </button>
  );

  const canContinue =
    (mode === "provision" && provisionedNumber) ||
    (mode === "existing" && data.selected_number) ||
    (mode === "manual" && manualNumber);

  const handleContinue = () => {
    if (mode === "manual") {
      setData((p) => ({ ...p, phone_number: manualNumber, phone_configured: true }));
    } else if (mode === "existing") {
      setData((p) => ({ ...p, phone_configured: true }));
    }
    onContinue();
  };

  return (
    <StepCard>
      <h2 className="text-xl font-bold text-white font-outfit mb-1">
        Set up your clinic phone number
      </h2>
      <p className="text-slate-400 text-sm mb-6">
        Choose how you want to handle incoming patient calls.
      </p>

      <div className="space-y-3">
        <RadioCard
          value="provision"
          title="Provision a new number"
          subtitle="We'll buy a US number via your Vapi account"
        />
        {mode === "provision" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="ml-8 space-y-3"
          >
            <div>
              <Label>Area Code</Label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                  placeholder="415"
                  maxLength={3}
                  className={`${inputCls} w-24`}
                />
                <button
                  onClick={handleProvision}
                  disabled={provisioning || !areaCode}
                  className="flex items-center gap-2 bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 px-4 py-2 rounded-lg text-sm disabled:opacity-50 transition-all"
                >
                  {provisioning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Phone className="w-4 h-4" />
                  )}
                  Provision Number
                </button>
              </div>
            </div>
            {provisionedNumber && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-green-400 text-sm font-semibold"
              >
                <CheckCircle className="inline w-3.5 h-3.5 mr-1" />
                {provisionedNumber}
              </motion.p>
            )}
          </motion.div>
        )}

        <RadioCard
          value="existing"
          title="Use existing Vapi number"
          subtitle="Select a number already in your Vapi account"
        />
        {mode === "existing" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="ml-8"
          >
            {loadingNumbers ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading numbers…
              </div>
            ) : phoneNumbers.length === 0 ? (
              <div className="flex items-center gap-3">
                <p className="text-slate-500 text-sm">No numbers found in your Vapi account.</p>
                <button
                  type="button"
                  onClick={fetchNumbers}
                  className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retry
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={data.selected_number ?? ""}
                  onChange={(e) =>
                    setData((p) => ({ ...p, selected_number: e.target.value }))
                  }
                  className={`${inputCls} appearance-none cursor-pointer`}
                >
                  <option value="" disabled className="bg-[#0A0D14] text-slate-500">
                    Select a number
                  </option>
                  {phoneNumbers.map((n) => (
                    <option key={n.id ?? n} value={n.number ?? n.phoneNumber ?? n} className="bg-[#0A0D14] text-white">
                      {n.number ?? n.phoneNumber ?? n}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={fetchNumbers}
                  title="Refresh"
                  className="p-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-slate-400 transition-colors flex-shrink-0"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        <RadioCard
          value="manual"
          title="Enter number manually"
          subtitle="Use your own phone number or forwarding number"
        />
        {mode === "manual" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="ml-8"
          >
            <input
              type="text"
              value={manualNumber}
              onChange={(e) => setManualNumber(e.target.value)}
              placeholder="+1 (415) 555-0100"
              className={inputCls}
            />
          </motion.div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold px-6 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </button>
        <BackButton onBack={onBack} />
        <button
          onClick={onSkip}
          className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
        >
          Skip for now
        </button>
      </div>
    </StepCard>
  );
}

/* ── Step 4: Integrations ── */
function Step4({ data, setData, onContinue, onSkip, onBack }) {
  const [gcalLoading, setGcalLoading] = useState(false);
  const [calendlyTesting, setCalendlyTesting] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(null); // null=loading, true/false

  // Check if Google OAuth is configured on the platform
  useEffect(() => {
    axios.get(`${API}/integrations/google/status`, { headers: authHeader() })
      .then((r) => {
        setGoogleConfigured(r.data.configured);
        if (r.data.connected) setData((p) => ({ ...p, google_connected: true }));
      })
      .catch(() => setGoogleConfigured(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for popup postMessage when Google OAuth completes
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "google_connected") {
        setData((p) => ({ ...p, google_connected: true }));
        toast.success(`Google Calendar connected${e.data.email ? ` (${e.data.email})` : ""}!`);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [setData]);

  const handleGoogleConnect = async () => {
    setGcalLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/integrations/google/auth-url`, {
        headers: authHeader(),
      });
      const popup = window.open(res.auth_url ?? res.url, "google_oauth", "width=500,height=650");
      if (!popup) toast.error("Popup blocked — please allow popups for this site.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not get Google auth URL.";
      if (msg.includes("not configured")) {
        toast.error("Google OAuth not set up yet. Configure it in Settings → Integrations after onboarding.");
      } else {
        toast.error(msg);
      }
    } finally {
      setGcalLoading(false);
    }
  };

  const handleCalendlyTest = async () => {
    if (!data.calendly_key) return toast.error("Enter your Calendly API key first.");
    setCalendlyTesting(true);
    try {
      const res = await axios.post(
        `${API}/integrations/calendly/test`,
        { api_key: data.calendly_key },
        { headers: authHeader() }
      );
      setData((p) => ({ ...p, calendly_connected: true }));
      toast.success(`Calendly connected! (${res.data?.name || res.data?.email || "OK"})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid Calendly API key.");
    } finally {
      setCalendlyTesting(false);
    }
  };

  const toggle = (key) =>
    setData((p) => ({ ...p, [key]: !p[key] }));

  const IntegrationTile = ({ icon: Icon, iconColor, title, subtitle, children }) => (
    <div className="flex items-start gap-4 p-4 bg-white/3 border border-white/10 rounded-xl">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconColor}`}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold">{title}</p>
        <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );

  return (
    <StepCard>
      <h2 className="text-xl font-bold text-white font-outfit mb-1">
        Connect your tools{" "}
        <span className="text-slate-500 font-normal text-base">(optional)</span>
      </h2>
      <p className="text-slate-400 text-sm mb-6">
        You can always set these up later in Settings.
      </p>

      <div className="space-y-3">
        {/* Google Calendar */}
        <IntegrationTile
          icon={Calendar}
          iconColor="bg-blue-500/20 border border-blue-500/30"
          title="Google Calendar"
          subtitle="Sync appointments automatically"
        >
          <div className="flex items-center gap-3 flex-wrap">
            {data.google_connected ? (
              <span className="flex items-center gap-1.5 text-green-400 text-xs font-medium">
                <CheckCircle className="w-3.5 h-3.5" /> Connected
              </span>
            ) : googleConfigured === false ? (
              <span className="text-slate-500 text-xs">
                Not configured —{" "}
                <a href="/admin/settings" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">
                  set up in Settings
                </a>{" "}
                after onboarding
              </span>
            ) : (
              <button
                onClick={handleGoogleConnect}
                disabled={gcalLoading || googleConfigured === null}
                className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50 transition-all"
              >
                {gcalLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Globe className="w-3.5 h-3.5" />
                )}
                Connect Google
              </button>
            )}
          </div>
        </IntegrationTile>

        {/* Calendly */}
        <IntegrationTile
          icon={Zap}
          iconColor="bg-violet-500/20 border border-violet-500/30"
          title="Calendly"
          subtitle="Accept online bookings"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggle("calendly_enabled")}
                className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                  data.calendly_enabled ? "bg-cyan-500" : "bg-white/10"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    data.calendly_enabled ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            {data.calendly_enabled && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={data.calendly_key ?? ""}
                    onChange={(e) =>
                      setData((p) => ({ ...p, calendly_key: e.target.value, calendly_connected: false }))
                    }
                    placeholder="Paste your Calendly Personal Access Token"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={handleCalendlyTest}
                    disabled={calendlyTesting || !data.calendly_key}
                    className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 px-3 py-2 rounded-lg text-xs disabled:opacity-50 transition-all flex-shrink-0"
                  >
                    {calendlyTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                    Test
                  </button>
                </div>
                {data.calendly_connected && (
                  <p className="flex items-center gap-1 text-green-400 text-xs">
                    <CheckCircle className="w-3.5 h-3.5" /> Connected
                  </p>
                )}
              </motion.div>
            )}
          </div>
        </IntegrationTile>

        {/* WhatsApp */}
        <IntegrationTile
          icon={MessageSquare}
          iconColor="bg-green-500/20 border border-green-500/30"
          title="WhatsApp"
          subtitle="Send appointment reminders"
        >
          <a
            href="/admin/settings"
            className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            Configure in Settings →
          </a>
        </IntegrationTile>
      </div>

      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={onContinue}
          className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity"
        >
          Continue
          <ChevronRight className="w-4 h-4" />
        </button>
        <BackButton onBack={onBack} />
        <button
          onClick={onSkip}
          className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
        >
          Skip all
        </button>
      </div>
    </StepCard>
  );
}

/* ── Step 5: Complete ── */
function Step5({ stepData, onFinish, finishing }) {
  const clinicName =
    stepData[1]?.clinic_name ||
    localStorage.getItem("clinic_name") ||
    "Your Clinic";

  const summary = [
    {
      label: "Clinic details",
      done: !!(stepData[1]?.clinic_name || stepData[1]?.phone),
    },
    {
      label: "Vapi AI connected",
      done: !!stepData[2]?.vapi_tested,
    },
    {
      label: "Phone number configured",
      done: !!stepData[3]?.phone_configured,
    },
    {
      label: "Integrations set up",
      done: !!(stepData[4]?.gcal_enabled || stepData[4]?.calendly_enabled),
    },
  ];

  const PARTICLES = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 1.2,
    color: i % 2 === 0 ? "#22d3ee" : "#7c3aed",
    size: 4 + Math.random() * 6,
  }));

  return (
    <StepCard>
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-b from-cyan-500/5 to-violet-500/5 border border-white/5 p-8 mb-6 text-center">
        {PARTICLES.map((p) => (
          <motion.div
            key={p.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${p.x}%`,
              bottom: 0,
              width: p.size,
              height: p.size,
              background: p.color,
            }}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: -220, opacity: [0, 1, 0] }}
            transition={{
              duration: 2.4,
              delay: p.delay,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
        ))}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
          className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/30"
        >
          <CheckCircle className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-2xl font-bold text-white font-outfit">
          Careline is ready for{" "}
          <span className="bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent">
            {clinicName}
          </span>
          !
        </h2>
        <p className="text-slate-400 text-sm mt-2">
          Your AI-powered clinic is live. Here's what we set up:
        </p>
      </div>

      <div className="space-y-2 mb-8">
        {summary.map(({ label, done }) => (
          <div key={label} className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                done
                  ? "bg-green-500/20 border border-green-500/40"
                  : "bg-white/5 border border-white/10"
              }`}
            >
              {done ? (
                <Check className="w-3 h-3 text-green-400" />
              ) : (
                <X className="w-3 h-3 text-slate-600" />
              )}
            </div>
            <span className={`text-sm ${done ? "text-slate-200" : "text-slate-500"}`}>
              {label}
            </span>
            {!done && (
              <span className="text-xs text-slate-600 ml-auto">Skipped</span>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={onFinish}
        disabled={finishing}
        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity shadow-lg shadow-cyan-500/20 text-base"
      >
        {finishing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            Go to Dashboard
            <ChevronRight className="w-5 h-5" />
          </>
        )}
      </button>
    </StepCard>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Onboarding component
══════════════════════════════════════════════════════════════ */
export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [finishing, setFinishing] = useState(false);

  const [stepData, setStepData] = useState({
    1: {
      clinic_name: localStorage.getItem("clinic_name") || "",
      phone: "",
      address: "",
      timezone: "America/New_York",
    },
    2: { vapi_key: "", vapi_tested: false },
    3: { phone_number: "", phone_configured: false, selected_number: "" },
    4: {
      gcal_enabled: false,
      google_connected: false,
      calendly_enabled: false,
      calendly_key: "",
    },
  });

  const setStepField = (step) => (updater) =>
    setStepData((p) => ({ ...p, [step]: updater(p[step]) }));

  const saveStep = async (nextStep, extraPayload = {}) => {
    try {
      await axios.put(
        `${API}/onboarding/update`,
        { step: nextStep, ...extraPayload },
        { headers: authHeader() }
      );
    } catch (_) {
      // non-fatal
    }
    setCurrentStep(nextStep);
  };

  const handleContinue = () => {
    if (currentStep < TOTAL_STEPS) saveStep(currentStep + 1, stepData[currentStep]);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((p) => p - 1);
  };

  const handleSkip = () => {
    if (currentStep < TOTAL_STEPS) saveStep(currentStep + 1, { skipped: true });
  };

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await axios.put(
        `${API}/onboarding/update`,
        { completed: true },
        { headers: authHeader() }
      );
      localStorage.setItem("onboarding_completed", "true");
      toast.success("Welcome to Careline!");
      navigate("/admin");
    } catch (_) {
      localStorage.setItem("onboarding_completed", "true");
      navigate("/admin");
    } finally {
      setFinishing(false);
    }
  };

  const STEP_LABELS = [
    "Clinic Details",
    "Connect Vapi",
    "Phone Number",
    "Integrations",
    "All Set!",
  ];

  return (
    <div className="min-h-screen bg-[#0F1117] flex flex-col overflow-hidden relative">
      {/* Ambient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-cyan-500/4 blur-3xl animate-pulse" />
        <div
          className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-violet-500/4 blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10 border-b border-white/5 px-6 py-4">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Phone className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold font-outfit text-white tracking-tight">Careline</span>
          </div>
          <span className="text-slate-500 text-xs">
            Step {currentStep} of {TOTAL_STEPS} — {STEP_LABELS[currentStep - 1]}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-xl">
          <ProgressBar current={currentStep} />

          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div key="s1">
                <Step1
                  data={stepData[1]}
                  setData={setStepField(1)}
                  onContinue={handleContinue}
                  onBack={null}
                />
              </motion.div>
            )}
            {currentStep === 2 && (
              <motion.div key="s2">
                <Step2
                  data={stepData[2]}
                  setData={setStepField(2)}
                  onContinue={handleContinue}
                  onSkip={handleSkip}
                  onBack={handleBack}
                />
              </motion.div>
            )}
            {currentStep === 3 && (
              <motion.div key="s3">
                <Step3
                  data={stepData[3]}
                  setData={setStepField(3)}
                  vapiKey={stepData[2].vapi_key}
                  onContinue={handleContinue}
                  onSkip={handleSkip}
                  onBack={handleBack}
                />
              </motion.div>
            )}
            {currentStep === 4 && (
              <motion.div key="s4">
                <Step4
                  data={stepData[4]}
                  setData={setStepField(4)}
                  onContinue={handleContinue}
                  onSkip={handleSkip}
                  onBack={handleBack}
                />
              </motion.div>
            )}
            {currentStep === 5 && (
              <motion.div key="s5">
                <Step5
                  stepData={stepData}
                  onFinish={handleFinish}
                  finishing={finishing}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
